if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // --- Elementos base
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        // --- Contexto de sección y variantes JSON
        const productInfo = this.closest('product-info');
        this.sectionId = productInfo?.dataset.section || '';
        this.variants = this._readVariantsJSON(productInfo, this.sectionId);

        // --- Input oculto id
        this.variantIdInput = this.form.querySelector('input[name="id"]');
        if (!this.variantIdInput) {
          this.variantIdInput = document.createElement('input');
          this.variantIdInput.type = 'hidden';
          this.variantIdInput.name = 'id';
          this.form.appendChild(this.variantIdInput);
        }
        this.variantIdInput.disabled = false;

        // --- Listeners
        this.form.addEventListener('change', this.onOptionsChange.bind(this));
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Estado inicial
        this.onOptionsChange();
      }

      // ======================
      //   VARIANTES
      // ======================
      _readVariantsJSON(productInfo, sectionId) {
        try {
          const script = productInfo?.querySelector(
            `#ProductVariants-${sectionId}`
          );
          if (!script) return [];
          return JSON.parse(script.textContent || '[]');
        } catch (e) {
          console.error('No se pudieron leer las variantes JSON', e);
          return [];
        }
      }

      _getSelectedOptionsByPosition() {
        const groups = Array.from(
          this.form.querySelectorAll('[name^="options["]')
        ).reduce((map, el) => {
          const match = el.name.match(/^options\[(.*)\]$/);
          const optionName = match ? match[1] : '';
          if (!optionName) return map;
          if (!map.has(optionName)) map.set(optionName, []);
          map.get(optionName).push(el);
          return map;
        }, new Map());

        const chosen = [];
        groups.forEach((elements) => {
          let value = null;
          const first = elements[0];
          if (first.type === 'radio') {
            const checked = elements.find((e) => e.checked);
            value = checked ? checked.value : null;
          } else if (first.tagName === 'SELECT') {
            value = first.value;
          } else {
            value = first.value;
          }
          chosen.push(value);
        });

        return chosen;
      }

      _findVariantByOptions(selectedOptions) {
        if (!Array.isArray(this.variants) || this.variants.length === 0)
          return null;
        return (
          this.variants.find((v) => {
            if (!Array.isArray(v.options)) return false;
            if (v.options.length !== selectedOptions.length) return false;
            for (let i = 0; i < v.options.length; i++) {
              if (String(v.options[i]) !== String(selectedOptions[i]))
                return false;
            }
            return true;
          }) || null
        );
      }

      // ======================
      //   BOTÓN
      // ======================
      updateButtonState(variant) {
        if (!this.submitButton || !this.submitButtonText) return;

        if (variant && variant.available) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            (window.themeStrings && window.themeStrings.addToCart) ||
            'Añadir a la cesta';
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent =
            (window.themeStrings && window.themeStrings.soldOut) || 'Agotado';
        }
      }

      // ======================
      //   EVENTOS
      // ======================
      onOptionsChange() {
        const selectedOptions = this._getSelectedOptionsByPosition();
        const current = this._findVariantByOptions(selectedOptions);

        if (current) {
          this.variantIdInput.value = current.id;
          this.updateButtonState(current);
        } else {
          this.updateButtonState(null);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton?.setAttribute('aria-disabled', 'true');
        this.submitButton?.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = this._fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        const selectedOptions = this._getSelectedOptionsByPosition();
        const currentVariant = this._findVariantByOptions(selectedOptions);
        if (currentVariant?.id) {
          formData.set('id', currentVariant.id);
          this.variantIdInput.value = String(currentVariant.id);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((s) => s.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              this.updateButtonState(null);
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton?.classList.remove('loading');
            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            const spinner2 = this.querySelector('.loading__spinner');
            if (spinner2) spinner2.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      // ======================
      //   ERRORES
      // ======================
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      _fetchConfig(type = 'json') {
        return {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: `application/${type}`,
          },
        };
      }
    }
  );
}
